import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { blockchainfeatures } from "../services";
import { hostURL } from "../services/data_requests";
import { toast } from "react-toastify";

const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [ethAddress, setEthAddressState] = useState(null);
  const { loginWithWeb3, refreshUser } = useAuth();

  useEffect(() => {
    let cancelled = false;

    // The address in localStorage is the one that signed in through web3_auth.
    // Only show it while MetaMask actually reports it as a live connected
    // account - otherwise the header must offer "Connect Wallet" instead.
    const resolveAddress = (accounts) => {
      const authed = localStorage.getItem("ethAddress");
      if (!authed) return null;

      const isLive = (accounts || []).some(
        (account) => account.toLowerCase() === authed.toLowerCase(),
      );
      return isLive ? authed : null;
    };

    const verifyConnection = async (accounts) => {
      if (!window.ethereum) {
        if (!cancelled) setEthAddressState(null);
        return;
      }

      try {
        const liveAccounts =
          accounts ||
          (await window.ethereum.request({ method: "eth_accounts" }));
        if (!cancelled) setEthAddressState(resolveAddress(liveAccounts));
      } catch (err) {
        console.error("Failed to read wallet accounts:", err);
        if (!cancelled) setEthAddressState(null);
      }
    };

    verifyConnection();

    if (window.ethereum) {
      const handleAccounts = (accounts) => verifyConnection(accounts);

      window.ethereum.on("accountsChanged", handleAccounts);
      return () => {
        cancelled = true;
        window.ethereum.removeListener("accountsChanged", handleAccounts);
      };
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const connectWallet = useCallback(async () => {
    if (isConnecting) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isWeb3Browser = !!window.ethereum;

    if (isMobile && !isWeb3Browser) {
      const currentUrl = window.location.href.replace(/^https?:\/\//, "");
      const metamaskAppDeepLink = `https://metamask.app.link/dapp/${currentUrl}`;

      window.location.href = metamaskAppDeepLink;
      return;
    }

    setIsConnecting(true);
    try {
      const { provider: p, address } = await blockchainfeatures.connectWallet();

      await blockchainfeatures.signMessageAndSend({
        provider: p,
        address,
        endpoint: `${hostURL}web3_auth`,
      });

      loginWithWeb3(address);
      setEthAddressState(address);
      // Pull the profile behind the new session so the context isn't left
      // holding only the address.
      refreshUser();
      toast.success("Connected!");
    } catch (err) {
      console.error("Connection Error:", err);
      toast.error("❌ " + (err.message || "Failed to connect"));
    } finally {
      setIsConnecting(false);
    }
  }, [isConnecting, loginWithWeb3, refreshUser]);

  return (
    <WalletContext.Provider
      value={{
        isConnecting,
        connectWallet,
        ethAddress,
        isWalletConnected: !!ethAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => useContext(WalletContext);
